import { Component, inject, input, computed } from "@angular/core";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { DragDropModule, CdkDrag } from "@angular/cdk/drag-drop";
import { WkDataTableContextService } from "@components/data-table/data-table-context.service";
import { WkDataTableDraggingService } from "./data-table-dragging.service";
import { WkDataTableStoreService } from "@components/data-table/store";
import { WkDataTableSortService } from "@app/shared/components/data-table/item";
import { WkOverflowTooltipDirective } from "@shared/directives";
import { WkDataTableSortDirectionEnum } from "@components/data-table/data-table.types";
import { ColumnFilterComponent } from "@filter";

@Component({
    selector: "th[wk-data-table-column]",
    templateUrl: "./data-table-column.component.html",
    styleUrls: ["./data-table-column.component.scss", "../data-table-cell.scss"],
    host: {
        class: "wk-data-table__cell wk-data-table__heading-cell",
        "[style.width.px]": "width()",
        "[class.wk-data-table__cell--locked]": "locked()",
        "[style.left.px]": "lockedPosition()",
        "[class.wk-data-table__heading-cell--sortable]": "sortable()",
        "[class.wk-data-table__heading-cell--filterable]": "filterable()",
        "[class.wk-data-table__heading-cell--sorted]": "!!sortInfo()",
        "[class.wk-data-table__heading-cell--sorted-asc]": "sortInfo()?.direction === SortDirectionEnum.Asc",
        "[class.wk-data-table__heading-cell--sorted-desc]": "sortInfo()?.direction === SortDirectionEnum.Desc",
        "[class.wk-data-table__heading-cell--multiple-sort]": "dataTableSort.sorting()?.multiple",
        "(cdkDragStarted)": "dataTableDragging.onColumnDragStarted()",
        "(cdkDragEnded)": "dataTableDragging.onColumnDragEnded()",
        "[id]": "'wk-data-table__heading-cell-' + fieldname()",
    },
    imports: [JumpstartComponentsModule, DragDropModule, WkOverflowTooltipDirective, ColumnFilterComponent],
    hostDirectives: [
        {
            directive: WkOverflowTooltipDirective,
            inputs: ["wkOverflowTooltipTarget"],
        },
        {
            directive: CdkDrag,
            inputs: ["cdkDragDisabled", "cdkDragLockAxis"],
            outputs: ["cdkDragStarted", "cdkDragEnded"],
        },
    ],
})
export class WkDataTableColumn<TItem> {
    private readonly dataTableStore = inject(WkDataTableStoreService);
    private readonly dataTableContext = inject<WkDataTableContextService<TItem>>(WkDataTableContextService);
    protected readonly dataTableSort = inject<WkDataTableSortService<TItem>>(WkDataTableSortService);
    protected readonly dataTableDragging = inject(WkDataTableDraggingService);
    protected readonly SortDirectionEnum = WkDataTableSortDirectionEnum;

    protected readonly wkOverflowTooltipTarget = ".wk-data-table__cell__value";
    protected readonly cdkDragLockAxis = "x";
    get cdkDragDisabled(): boolean {
        return !this.reorderableColumns();
    }

    public index = input.required<number>();
    public fieldname = input.required<string>();

    protected readonly column = computed(() => this.dataTableStore.get(this.fieldname()));
    protected readonly width = computed(() => this.column()?.definition.width ?? 170);
    protected locked = computed(() => this.column()?.definition.locked ?? false);
    protected lockedPosition = computed(() => {
        const locked = this.locked();
        const index = this.index();
        const columns = this.dataTableStore.lockedColumns();
        const selectable = this.dataTableContext.selectable();
        if (!locked) return 0;
        const lockedColumns = columns.filter((_, colIndex) => colIndex < index);
        let position = selectable ? 48 : 0;
        lockedColumns.forEach((col) => {
            position += col.definition.width ?? 170;
        });
        return position;
    });
    protected title = computed(() => this.column()?.getTitle() ?? "");
    protected sortable = computed(() => this.column()?.definition.sortable ?? true);
    protected filterable = computed(() => this.column()?.definition.filterable ?? false);
    protected reorderableColumns = computed(() => this.dataTableContext.reorderableColumns());
    protected sortInfo = computed(() => this.dataTableSort.items()?.get?.(this.fieldname()));
}
